<template>
  <div>
    <navbar :links="['dashboard', 'contents', $route.name]" />
    <!-- Loading Indicator -->
    <div
      v-if="loading"
      class="loading-indicator mt-5"
    >
      <v-progress-circular
        :size="50"
        indeterminate
        class="msr--text"
      />
    </div>
    <!-- Header Hint -->
    <div
      v-if="!loading"
      class="subheading grey--text text--darken-2 mt-4"
      data-cy="brand-group-selection-hint-text"
    >
      {{ headerHint }}
    </div>
    <!-- Warning about selected Groups without Content -->
    <v-slide-y-transition>
      <v-layout
        v-if="
          !loading &&
            atLeastOneSelectedBrandHasNoContent &&
            !isSuppressedSelectedBrandGroupsWithoutContentWarning()
        "
        class="body-1 warning--text pt-3 pb-2"
        column
        align-end
      >
        <v-layout align-start>
          <v-icon
            x-large
            color="warning"
            class="mr-3"
          >
            error_outline
          </v-icon>
          <span>
            {{ $t('selectedBrandGroupsWithoutContentWarning') }}
            <v-layout justify-space-between>
              <div class="font-weight-bold mt-2">
                {{
                  selectedBrandGroupsWithoutContent
                    .map((g) => g.name)
                    .join(', ')
                }}
              </div>
              <v-btn
                small
                round
                flat
                color="grey darken-2"
                class="my-0 mx-0"
                @click="
                  loading = true;
                  suppressSelectedBrandGroupsWithoutContentWarning();
                  loading = false;
                "
              >
                <v-icon
                  small
                  class="mr-1"
                > close </v-icon>
                {{
                  $vuetify.breakpoint.xs
                    ? $t('suppressWarningForBrowserSessionShort')
                    : $t('suppressWarningForBrowserSession')
                }}
              </v-btn>
            </v-layout>
          </span>
        </v-layout>
      </v-layout>
    </v-slide-y-transition>
    <v-layout
      v-if="hasAvailableBrandGroups && !loading"
      column
      class="mt-3"
    >
      <v-layout
        align-center
        wrap
      >
        <v-text-field
          v-model="searchText"
          class="mr-3 my-0 py-0"
          no-filter
          clearable
          hide-no-data
          append-icon="search"
          data-cy="brand-groups-selection-search-input"
          :class="{'highlight-append-icon': !!searchText}"
          :label="searchLabel"
          @keyup.escape="clearSearch()"
        />
        <v-layout
          align-center
          wrap
          :justify-center="$vuetify.breakpoint.xsOnly"
          :class="{'mt-2': $vuetify.breakpoint.xsOnly}"
        >
          <v-checkbox
            v-model="showOnlySelected"
            class="py-0 mr-2"
            :class="{'no-checkbox-margin-top': $vuetify.breakpoint.smAndUp}"
            color="msr"
            hide-details
            data-cy="brand-groups-selection-show-selected-only-checkbox"
            :disabled="!showOnlySelected && selectedBrandGroups.length === 0"
            :label="$t('showOnlySelectedBrandGroups')"
          />
          <v-btn
            v-if="showBatchSelectionButtons"
            flat
            round
            color="msr"
            class="mr-0"
            data-cy="brand-groups-selection-select-all-btn"
            :disabled="showOnlySelected"
            @click="selectAllVisibleBrandGroups()"
          >
            {{ $t('selectAll') }}
          </v-btn>
          <v-btn
            v-if="showBatchSelectionButtons"
            flat
            round
            color="msr"
            class="mr-0"
            data-cy="brand-groups-selection-select-none-btn"
            :disabled="showOnlySelected"
            @click="deselectAllVisibleBrandGroups()"
          >
            {{ $t('selectNone') }}
          </v-btn>
        </v-layout>
      </v-layout>
    </v-layout>
    <v-layout
      v-if="!loading"
      wrap
      :justify-center="$vuetify.breakpoint.xsOnly"
      class="mt-4"
    >
      <brand-group-card
        v-for="(group, index) in filteredBrandGroups"
        :id="`brand-group-card-${group.id}`"
        :key="index"
        data-cy="brand-group-card"
        :group="group"
        :selected="!!brandGroupIdToSelected[group.id]"
        @toggled="toggleGroup(group)"
      />
    </v-layout>
  </div>
</template>

<script>
import goTo from 'vuetify/es5/components/Vuetify/goTo';
import orderBy from 'lodash.orderby';

import suppressWarnings from '@/mixins/suppressWarnings.js';

import initializeFuseJsInstance from '@/fuzzySearch';

import BrandGroupCard from '@/components/BrandGroupCard';
import Navbar from '@/components/Navbar';

export default {
  components: {
    BrandGroupCard,
    Navbar,
  },
  mixins: [suppressWarnings],
  data: () => ({
    loading: false,
    searchText: '',
    showOnlySelected: false,
    maxAvailableBrandsForBatchSelection: 50,

    fuse: null,
  }),
  computed: {
    selectedBrandGroupsWithoutContent() {
      return this.selectedBrandGroups.filter((group) => {
        return group.num_radiospots + group.num_tvspots == 0;
      });
    },
    atLeastOneSelectedBrandHasNoContent() {
      return this.selectedBrandGroupsWithoutContent.length > 0;
    },
    showBatchSelectionButtons() {
      return (
        this.availableBrandGroups.length <=
        this.maxAvailableBrandsForBatchSelection
      );
    },
    hasAvailableBrandGroups() {
      return this.availableBrandGroups.length > 0;
    },
    headerHint() {
      if (!this.hasAvailableBrandGroups) {
        return this.$t('noAvailableBrandGroups');
      }
      return (
        this.$t('chosenBrandGroupsHint_1') +
        ' ' +
        this.$t('chosenBrandGroupsHint_2')
          .replace('{{ numChosen }}', this.chosenBrandGroupIds.length)
          .replace('{{ numAvailable }}', this.availableBrandGroups.length)
      );
    },
    searchLabel() {
      return `${this.$t('search')} (${this.filteredBrandGroups.length}) ...`;
    },
    availableBrandGroups() {
      if (!this.$user) {
        return [];
      }
      return this.$userprofile.available_brand_groups;
    },
    sortedBrandGroups() {
      return orderBy(this.availableBrandGroups, [(g) => g.name.toLowerCase()]);
    },
    filteredBrandGroups() {
      let groups = this.sortedBrandGroups;

      const search = (this.searchText || '').trim().toLowerCase();
      if (search) {
        if (!this.fuse) {
          this.initFuzzySearch();
        }
        groups = this.fuse.search(search);
      }

      groups = groups.filter((group) => {
        if (this.showOnlySelected && !this.isGroupSelected(group)) {
          return false;
        }
        return true;
      });

      return groups;
    },
    selectedBrandGroups() {
      return this.sortedBrandGroups.filter((g) => this.isGroupSelected(g));
    },
    chosenBrandGroupIds: {
      get() {
        if (!this.$user) {
          return [];
        }
        return this.$userprofile.chosen_brand_groups;
      },
      set(ids) {
        // We update the ids list in the store directly, saving us
        // having to refetch the User on every change. Update frontend
        // first to see results immediately, then send an update to the
        // server.
        this.$store.commit('SET_USER_CHOSEN_BRAND_GROUPS', ids);

        // Invalidate contents list as a side effect.
        this.$store.commit('RESET_LAST_CONTENTS_QUERY');

        this.$api.updateChosenBrandGroups(ids);
      },
    },
    brandGroupIdToSelected() {
      return this.$store.state.common.brandGroupIdToSelected;
    },
  },
  watch: {
    chosenBrandGroupIds: function (ids) {
      if (ids.length === 0) {
        this.showOnlySelected = false;
      }
    },
  },
  created() {
    this.fetchUserAndInitializeSelection();
  },
  methods: {
    initFuzzySearch() {
      this.fuse = initializeFuseJsInstance(this.sortedBrandGroups, ['name']);
    },
    fetchUserAndInitializeSelection() {
      this.loading = true;
      const promise = this.$store.dispatch('RETRIEVE_CURRENT_USER').then(() => {
        const ids = this.$userprofile.chosen_brand_groups;
        this.$store.commit('SET_USER_CHOSEN_BRAND_GROUPS', ids);
      });
      promise.finally(() => (this.loading = false));
      return promise;
    },
    clearSearch() {
      this.searchText = '';
    },
    isGroupSelected(group) {
      return this.brandGroupIdToSelected[group.id];
    },
    toggleGroup(group) {
      if (this.isGroupSelected(group)) {
        this.deselectBrandGroup(group);
      } else {
        this.selectBrandGroup(group);
      }
    },
    selectBrandGroup(group) {
      const ids = this.chosenBrandGroupIds.slice();
      ids.push(group.id);
      this.chosenBrandGroupIds = ids;
    },
    deselectBrandGroup(group) {
      this.chosenBrandGroupIds = this.chosenBrandGroupIds.filter(
        (id) => id !== group.id
      );
    },
    selectAllVisibleBrandGroups() {
      const confirmed = confirm(this.$t('brandGroupsSelectAllWarning'));
      if (!confirmed) {
        return;
      }

      const visibleIds = this.filteredBrandGroups.map((g) => g.id);
      const chosenIds = this.chosenBrandGroupIds.slice();

      const newChosenIds = chosenIds;
      for (let visibleId of visibleIds) {
        const alreadySelected = chosenIds.indexOf(visibleId) !== -1;
        if (!alreadySelected) {
          newChosenIds.push(visibleId);
        }
      }

      this.chosenBrandGroupIds = newChosenIds;
    },
    deselectAllVisibleBrandGroups() {
      const confirmed = confirm(this.$t('brandGroupsDeselectAllWarning'));
      if (!confirmed) {
        return;
      }

      const visibleIds = this.filteredBrandGroups.map((g) => g.id);
      const chosenIds = this.chosenBrandGroupIds.slice();

      const newChosenIds = [];
      for (let chosenId of chosenIds) {
        const notVisible = visibleIds.indexOf(chosenId) === -1;
        if (notVisible) {
          newChosenIds.push(chosenId);
        }
      }

      this.chosenBrandGroupIds = newChosenIds;
    },
    /**
     * Optionally scroll to card matching given brand Group name.
     */
    handleScrollQuery() {
      const groupId = this.$route.query.scroll;
      if (groupId) {
        const selector = '#brand-group-card-' + groupId;
        const offset = 135 / 2.0; // Half of the card's height.
        goTo(selector, {offset: offset});
      }
    },
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.fetchUserAndInitializeSelection().then(() => {
        vm.handleScrollQuery();
      });
    });
  },
};
</script>

<style scoped>
.my-0 >>> .v-input__slot {
  margin: 0;
}

.highlight-append-icon >>> .v-input__icon--append i {
  color: var(--msr-color-primary);
}

.no-checkbox-margin-top.v-input--selection-controls {
  margin-top: 0;
}
</style>



// WEBPACK FOOTER //
// src/pages/BrandGroupSelection.vue